// ============================================================================
// FacilityNaContext — Set of `field_path` values currently marked Not Applicable
// for the active facility (from public.facility_field_na).
//
// Created 2026-05-22 alongside the AM Collect auto-promotion rewrite. Consumed
// by InlineEditField to:
//   1. Hide the row entirely in read mode when `hideBlanks=true` and the field
//      is NA-marked (treats NA fields the same as blank ones).
//   2. In edit mode, surface an "N/A" pill next to the input so AMs can see
//      the field was intentionally marked not-applicable. (Detoggling NA happens
//      by the AM typing a real value — the Edge Function deletes the marker
//      automatically; or by the AM clicking the small Clear button next to the
//      pill, which calls an onClearNa callback if provided.)
// ============================================================================

import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

interface FacilityNaContextValue {
  /** Set of `field_path` strings marked NA for the facility (from facility_field_na). */
  naFieldPaths: Set<string>;
  /** Whether the context has loaded data (false during loading, true after fetch). */
  loaded: boolean;
}

const FacilityNaContext = createContext<FacilityNaContextValue>({
  naFieldPaths: new Set(),
  loaded: false,
});

export function useFacilityNa(fieldPath: string | undefined | null): boolean {
  const { naFieldPaths } = useContext(FacilityNaContext);
  if (!fieldPath) return false;
  return naFieldPaths.has(fieldPath);
}

export function useFacilityNaContext(): FacilityNaContextValue {
  return useContext(FacilityNaContext);
}

interface ProviderProps {
  /** The raw rows from facility_field_na for this facility. Pass an empty array if not yet loaded. */
  naFieldPaths: string[];
  loaded?: boolean;
  children: ReactNode;
}

export function FacilityNaProvider({ naFieldPaths, loaded = true, children }: ProviderProps) {
  const value: FacilityNaContextValue = {
    naFieldPaths: new Set(naFieldPaths),
    loaded,
  };
  return <FacilityNaContext.Provider value={value}>{children}</FacilityNaContext.Provider>;
}
